import { useEffect, useId, useState, type FormEvent } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Input,
  cn,
} from '@eureca/ui';
import { XIcon } from '@eureca/ui/icons';
import { EyeIcon } from '@/features/account/profile/change-password/icons/EyeIcon';
import { EyeOffIcon } from '@/features/account/profile/change-password/icons/EyeOffIcon';
import { GoogleIcon } from '@/features/account/profile/linked-accounts/icons/GoogleIcon';
import { LinkedInIcon } from '@/features/account/profile/linked-accounts/icons/LinkedInIcon';

/**
 * DEV-555 — "Confirme sua identidade" (Figma Account › Story: DEV- › Verify identity).
 *
 * Mobile (< 1024 px): Drawer sobre a página de origem, com cabeçalho e rodapé,
 * SEM botão de fechar — só a alça (AC-17). Desktop: Dialog centralizado com
 * cabeçalho, rodapé e Dialog Close (AC-18). Cabeçalho idêntico nas 14 telas
 * (AC-19). Corpo por variante: senha (campo + olho), provedor (Google /
 * Linkedin — botão com o ícone à direita; Alert destrutivo quando a conta foi
 * outra) ou `none` (conta sem senha e sem provedor). Rodapé: ação primária +
 * "Sair da conta" (mobile empilhado, primária em cima; desktop à direita,
 * primária à direita).
 */
export const IDENTITY_CONFIRMATION_TITLE = 'Confirme sua identidade';
export const IDENTITY_CONFIRMATION_DESCRIPTION =
  'Esta é uma ação importante. Por segurança, confirme que é você antes de continuar.';
export const WRONG_PASSWORD_MESSAGE = 'Senha incorreta, tente de novo ou ';
export const WRONG_PASSWORD_LINK = 'redefina ela';
export const UNAVAILABLE_MESSAGE =
  'Não foi possível verificar sua senha agora. Tente de novo em instantes.';
/** SC-06 — trava por senhas erradas seguidas (espelho da força bruta do realm). */
export function tooManyAttemptsMessage(retryAfterSeconds: number): string {
  const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
  return `Muitas tentativas de senha. Aguarde ${minutes} minuto${minutes === 1 ? '' : 's'} e tente de novo, ou `;
}
export const NO_METHOD_MESSAGE =
  'Sua conta não tem senha nem conta social vinculada. Fale com o suporte para confirmar sua identidade.';

export type IdentityConfirmationMethod = 'password' | 'google' | 'linkedin' | 'none' | 'loading';
export type IdentityConfirmationError =
  | 'invalid-password'
  | 'too-many-attempts'
  | 'unavailable'
  | null;

export interface IdentityConfirmationProps {
  open: boolean;
  /** `true` = Dialog (≥ 1024 px); `false` = Drawer. */
  isDesktop: boolean;
  method: IdentityConfirmationMethod;
  /** Alerta do retorno do provedor com outra conta (AC-31). */
  providerError: 'different-account' | null;
  /** Erro da verificação por senha (AC-25 / AC-38 / SC-06). */
  error: IdentityConfirmationError;
  /** Espera informada pelo servidor na trava por tentativas (SC-06). */
  retryAfterSeconds?: number;
  /** Verificação em curso: ação primária em loading, sem segundo acionamento (AC-23). */
  loading: boolean;
  onSubmitPassword: (password: string) => void;
  onProvider: () => void;
  onLogout: () => void;
  /** Alça do Drawer / Dialog Close: volta à origem, nada executa (AC-34). */
  onDismiss: () => void;
  /** "redefina ela": fecha e vai ao fluxo de redefinição de senha (AC-26). */
  onResetPassword: () => void;
}

const providerLabel: Record<'google' | 'linkedin', string> = {
  google: 'Continuar com Google',
  linkedin: 'Continuar com Linkedin',
};

const providerAlert: Record<'google' | 'linkedin', string> = {
  google: 'Conta Google diferente da utilizada, use a correta e tente de novo.',
  linkedin: 'Conta Linkedin diferente da utilizada, use a correta e tente de novo.',
};

function PasswordField({
  value,
  onChange,
  hasError,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  hasError: boolean;
  disabled: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const id = useId();
  return (
    <div className="relative">
      <label htmlFor={id} className="sr-only">
        Senha
      </label>
      <Input
        id={id}
        type={visible ? 'text' : 'password'}
        placeholder="Digite sua senha"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn('pr-9', hasError && 'border-destructive')}
        autoComplete="current-password"
        disabled={disabled}
        aria-invalid={hasError || undefined}
        data-testid="identity-confirmation-password"
      />
      <button
        type="button"
        aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        data-testid="identity-confirmation-password-toggle"
      >
        {visible ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
      </button>
    </div>
  );
}

export function IdentityConfirmation(props: IdentityConfirmationProps) {
  const { open, isDesktop, method, providerError, error, loading, onDismiss } = props;
  const [password, setPassword] = useState('');
  // Ao redigitar, "Senha incorreta" sai da tela (volta se a nova tentativa errar).
  const [typedSinceError, setTypedSinceError] = useState(false);
  useEffect(() => setTypedSinceError(false), [error]);
  const passwordError = error === 'invalid-password' && !typedSinceError;
  const locked = error === 'too-many-attempts';

  function submit(event: FormEvent) {
    event.preventDefault();
    // AC-24 — campo vazio (inclusive só espaços): nada é enviado, a confirmação segue aberta.
    if (loading || password.trim().length === 0) return;
    props.onSubmitPassword(password);
  }

  const provider = method === 'google' || method === 'linkedin' ? method : null;

  const body = (
    <div className="flex flex-col gap-3 px-6" data-testid="identity-confirmation-body">
      {method === 'password' && (
        <form
          id="identity-confirmation-form"
          onSubmit={submit}
          className="flex flex-col gap-2"
          noValidate
        >
          <PasswordField
            value={password}
            onChange={(v) => {
              setPassword(v);
              setTypedSinceError(true);
            }}
            hasError={passwordError || locked}
            disabled={loading}
          />
          {locked && (
            <p
              className="text-sm text-muted-foreground"
              role="alert"
              data-testid="identity-confirmation-locked"
            >
              {tooManyAttemptsMessage(props.retryAfterSeconds ?? 60)}
              <button
                type="button"
                className="font-medium text-primary underline underline-offset-2"
                onClick={props.onResetPassword}
                data-testid="identity-confirmation-locked-reset"
              >
                {WRONG_PASSWORD_LINK}
              </button>
              .
            </p>
          )}
          {passwordError && (
            <p
              className="text-sm text-muted-foreground"
              role="alert"
              data-testid="identity-confirmation-error"
            >
              {WRONG_PASSWORD_MESSAGE}
              <button
                type="button"
                className="font-medium text-primary underline underline-offset-2"
                onClick={props.onResetPassword}
                data-testid="identity-confirmation-reset-password"
              >
                {WRONG_PASSWORD_LINK}
              </button>
              .
            </p>
          )}
          {error === 'unavailable' && (
            <Alert variant="destructive" data-testid="identity-confirmation-unavailable">
              <AlertDescription>{UNAVAILABLE_MESSAGE}</AlertDescription>
            </Alert>
          )}
        </form>
      )}
      {provider && providerError === 'different-account' && (
        <Alert variant="destructive" data-testid="identity-confirmation-provider-alert">
          <AlertDescription>{providerAlert[provider]}</AlertDescription>
        </Alert>
      )}
      {method === 'none' && (
        <Alert variant="destructive" data-testid="identity-confirmation-no-method">
          <AlertDescription>{NO_METHOD_MESSAGE}</AlertDescription>
        </Alert>
      )}
    </div>
  );

  const primary =
    method === 'password' ? (
      <Button
        type="submit"
        form="identity-confirmation-form"
        className={isDesktop ? undefined : 'w-full'}
        size={isDesktop ? 'sm' : undefined}
        loading={loading}
        disabled={loading}
        data-testid="identity-confirmation-confirm"
      >
        Confirmar
      </Button>
    ) : provider ? (
      <Button
        type="button"
        className={isDesktop ? undefined : 'w-full'}
        size={isDesktop ? 'sm' : undefined}
        loading={loading}
        disabled={loading}
        onClick={props.onProvider}
        data-testid={`identity-confirmation-provider-${provider}`}
      >
        {providerLabel[provider]}
        {provider === 'google' ? (
          <GoogleIcon className="size-4 text-current lg:size-4" aria-hidden />
        ) : (
          <LinkedInIcon className="size-4 text-current lg:size-4" aria-hidden />
        )}
      </Button>
    ) : null;

  const logout = (
    <Button
      type="button"
      variant="outline"
      className={isDesktop ? undefined : 'w-full'}
      size={isDesktop ? 'sm' : undefined}
      disabled={loading}
      onClick={props.onLogout}
      data-testid="identity-confirmation-logout"
    >
      Sair da conta
    </Button>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={(next) => !next && !loading && onDismiss()}>
        <DialogContent
          className="bottom-auto left-1/2 top-1/2 w-[calc(100vw-2rem)] max-w-[448px] -translate-x-1/2 -translate-y-1/2 rounded-xl"
          data-testid="identity-confirmation-dialog"
        >
          <DialogHeader className="gap-1.5 px-6 pt-6 text-left">
            <DialogTitle className="tracking-[-0.45px]">{IDENTITY_CONFIRMATION_TITLE}</DialogTitle>
            <DialogDescription>{IDENTITY_CONFIRMATION_DESCRIPTION}</DialogDescription>
          </DialogHeader>
          <div className="pt-4">{body}</div>
          <DialogFooter className="gap-2 px-6 pb-6 pt-4 sm:justify-end sm:gap-2">
            {logout}
            {primary}
          </DialogFooter>
          <DialogClose
            aria-label="Fechar"
            disabled={loading}
            className="absolute right-[15px] top-[15px] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            data-testid="identity-confirmation-close"
          >
            <XIcon className="size-4" />
            <span className="sr-only">Fechar</span>
          </DialogClose>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={(next) => !next && !loading && onDismiss()}>
      <DrawerContent data-testid="identity-confirmation-drawer">
        <DrawerHeader className="px-6 pb-2 pt-4 text-left">
          <DrawerTitle className="text-base font-medium leading-none text-card-foreground">
            {IDENTITY_CONFIRMATION_TITLE}
          </DrawerTitle>
          <DrawerDescription className="text-sm leading-5 text-muted-foreground">
            {IDENTITY_CONFIRMATION_DESCRIPTION}
          </DrawerDescription>
        </DrawerHeader>
        <div className="pt-2">{body}</div>
        <DrawerFooter className="gap-2 px-6 pb-6 pt-4">
          {primary}
          {logout}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
